/**
 * Built-in chart palettes: well-known, accessible sets (Tableau, ColorBrewer, Okabe-Ito,
 * Paul Tol, matplotlib's perceptual maps). Pure data plus helpers; no tenant values.
 *
 * A chart's look names a built-in as `{ kind: "built_in", value: <id> }`; the canvas
 * draws it with `applyChartPalette` (see `withBuiltInPalette`).
 *
 * - categorical: one distinct colour per series or category (bars, lines, pies).
 * - sequential:  low → high on one measure (heatmaps, choropleths, calendars).
 * - diverging:   below / above a midpoint (+/- measures, correlation).
 */

import builtInPalettes from "./builtInPalettes.json";

export type PaletteKind = "categorical" | "sequential" | "diverging";

export const PALETTE_KINDS: readonly PaletteKind[] = ["categorical", "sequential", "diverging"];

export interface PaletteDefinition {
  id: string;
  label: string;
  kind: PaletteKind;
  /** Hex colours in draw order (sequential: low → high; diverging: low → mid → high). */
  colors: readonly string[];
}

/**
 * The list itself is `builtInPalettes.json`. api-service holds the same file
 * (`src/service/chart_palettes.json`) to compile built-ins into server specs; a test in each
 * repo pins the same content hash, so the two can't drift apart unnoticed.
 */
export const BUILT_IN_PALETTES: readonly PaletteDefinition[] = builtInPalettes as PaletteDefinition[];

const BY_ID = new Map(BUILT_IN_PALETTES.map((palette) => [palette.id, palette]));

export const findBuiltInPalette = (id: string | null | undefined): PaletteDefinition | undefined =>
  id ? BY_ID.get(id) : undefined;

export const palettesByKind = (kind: PaletteKind): PaletteDefinition[] =>
  BUILT_IN_PALETTES.filter((palette) => palette.kind === kind);

/** Chart types whose colour is a measure's magnitude, not a category. */
const MAGNITUDE_TYPES: ReadonlySet<string> = new Set([
  "heatmap", "choropleth", "calendar", "correlation", "treemap", "symbol_map",
]);
/** Chart types that plot gains and losses around zero. */
const SIGNED_TYPES: ReadonlySet<string> = new Set(["waterfall", "butterfly", "correlation"]);

export interface PaletteKindOptions {
  /** The coloured measure runs both sides of zero (a +/- measure). */
  signed?: boolean;
}

/**
 * The palette kinds that suit a chart type, best first. Magnitude charts take sequential
 * (diverging too when signed); every other chart takes categorical, plus diverging when signed.
 */
export const paletteKindsForChartType = (
  chartType: string | null | undefined,
  { signed = false }: PaletteKindOptions = {},
): PaletteKind[] => {
  const type = chartType ?? "";
  const isSigned = signed || SIGNED_TYPES.has(type);
  if (MAGNITUDE_TYPES.has(type)) return isSigned ? ["diverging", "sequential"] : ["sequential", "diverging"];
  return isSigned ? ["categorical", "diverging"] : ["categorical"];
};

/** The built-ins a chart type can pick, grouped in `paletteKindsForChartType` order. */
export const palettesForChartType = (
  chartType: string | null | undefined,
  options?: PaletteKindOptions,
): PaletteDefinition[] => paletteKindsForChartType(chartType, options).flatMap(palettesByKind);

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** One colour → `#RRGGBB` upper-case, or `null` when it is not a hex colour. */
export const normalizeHex = (raw: string): string | null => {
  const match = HEX.exec(raw.trim());
  if (!match) return null;
  const digits = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  return `#${digits.toUpperCase()}`;
};

export interface ParsedHexList {
  colors: string[];
  /** Tokens that were not hex colours, as typed. */
  invalid: string[];
}

/**
 * Pasted text → hex colours. Accepts any mix of commas, spaces, new lines, quotes and
 * brackets (a CSS list, a JSON array, a column copied from a sheet); `#` is optional.
 */
export const parseHexList = (text: string): ParsedHexList => {
  const tokens = text.split(/[\s,;"'[\]()]+/).filter(Boolean);
  const colors: string[] = [];
  const invalid: string[] = [];
  tokens.forEach((token) => {
    const hex = normalizeHex(token);
    if (hex) colors.push(hex);
    else invalid.push(token);
  });
  return { colors, invalid };
};
